import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Cloud, Brain, Code, TrendingUp, Search, Shield, Zap, Users, 
  ArrowRight, CheckCircle, Star, ChevronRight, Award, Target,
  Server, Database, Globe, Cpu, Sparkles, Play
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { useSEO } from '@/hooks/useSEO';

const HERO_BG = "/assets/hero-bg.jpg";
const GCP_BADGE = "/assets/gcp_partner_badge.png";

const services = [
  {
    icon: Brain,
    title: 'AI Automation',
    description: 'Transform repetitive tasks into intelligent workflows. Our AI solutions learn, adapt, and scale with your business needs.',
    color: 'violet',
  },
  {
    icon: Cloud,
    title: 'Cloud Consulting',
    description: 'Design cloud architectures that actually work. Google Cloud certified experts who understand real-world infrastructure challenges.',
    color: 'cyan',
  },
  {
    icon: Code,
    title: 'Web Development',
    description: 'Build digital products that users love. From sleek websites to complex SaaS platforms—performance and design, unified.',
    color: 'blue',
  },
  {
    icon: TrendingUp,
    title: 'Marketing',
    description: 'Data-driven campaigns that convert. Performance marketing strategies refined through continuous testing and optimization.',
    color: 'teal',
  },
  {
    icon: Search,
    title: 'SEO & Growth',
    description: 'Get found by the customers who need you. Technical SEO, content strategy, and visibility optimization that drives organic traffic.',
    color: 'violet',
  },
];

const whyChooseUs = [
  { icon: Award, title: 'Certified Experts', description: 'Google Cloud certified team with real enterprise experience—not just certifications.' },
  { icon: Target, title: 'Results-Driven', description: 'Every solution measured by business impact. We succeed when you succeed.' },
  { icon: Shield, title: 'Secure & Compliant', description: 'Enterprise-grade security built-in. Your data protection is non-negotiable.' },
  { icon: Zap, title: 'Fast Delivery', description: 'Agile methodology for rapid deployment. Quick iterations, continuous improvement.' },
];

const testimonials = [
  {
    quote: "BikerTechie helped us reduce cloud costs by 40% while improving performance. Their team actually understands business needs, not just technology.",
    author: "Rajesh Kumar",
    role: "CTO, TechStart Solutions",
    rating: 5,
  },
  {
    quote: "The AI automation they built increased our team's productivity dramatically. What used to take hours now happens automatically. Game changer.",
    author: "Priya Sharma",
    role: "Operations Director, InnovateCo",
    rating: 5,
  },
  {
    quote: "Professional, knowledgeable, and genuinely invested in our success. They're not just vendors—they're partners who care about outcomes.",
    author: "Amit Patel",
    role: "Founder, DataDriven Inc",
    rating: 5,
  },
];

const fadeInUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0 }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: { staggerChildren: 0.1 }
  }
};

export const Home = () => {
  useSEO({
    title: 'BikerTechie — Google Cloud & AI Consulting Partner in India',
    description:
      'Certified Google Cloud consulting partner delivering AI automation, cloud migration, web development and SEO for businesses across India. Book a free consultation.',
    path: '/',
  });

  const navigate = useNavigate();

  const handleNavClick = (e, path) => {
    e.preventDefault();
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  return (
    <div className="min-h-screen bg-[#030712]" data-testid="home-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center overflow-hidden" data-testid="hero-section">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 opacity-20"
            style={{
              backgroundImage: `url(${HERO_BG})`,
              backgroundSize: 'cover',
              backgroundPosition: 'center',
            }}
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-950/90 via-slate-950/80 to-slate-950" />
          
          {/* Animated orbs */}
          <motion.div 
            className="absolute top-20 left-1/4 w-[600px] h-[600px] bg-violet-500/20 rounded-full blur-[120px]"
            animate={{ 
              scale: [1, 1.2, 1],
              x: [0, 50, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          <motion.div 
            className="absolute bottom-20 right-1/4 w-[500px] h-[500px] bg-cyan-500/15 rounded-full blur-[100px]"
            animate={{ 
              scale: [1.2, 1, 1.2],
              x: [0, -50, 0],
              opacity: [0.3, 0.5, 0.3]
            }}
            transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
          />
          
          {/* Floating particles */}
          {[...Array(6)].map((_, i) => (
            <motion.div
              key={i}
              className="absolute w-2 h-2 bg-violet-400/30 rounded-full"
              style={{
                top: `${20 + i * 15}%`,
                left: `${10 + i * 15}%`,
              }}
              animate={{
                y: [0, -30, 0],
                opacity: [0.3, 0.7, 0.3],
              }}
              transition={{
                duration: 3 + i * 0.5,
                repeat: Infinity,
                delay: i * 0.3,
              }}
            />
          ))}
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="text-center max-w-4xl mx-auto"
          >
            {/* Badge */}
            <motion.div variants={fadeInUp} className="mb-8">
              <motion.span 
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-violet-500/10 to-cyan-500/10 border border-violet-500/20 text-violet-300 text-sm"
                whileHover={{ scale: 1.02, borderColor: 'rgba(124, 58, 237, 0.4)' }}
              >
                <Sparkles size={16} className="animate-pulse" />
                Google Cloud Consulting Partner
              </motion.span>
            </motion.div>

            {/* Main Headline */}
            <motion.h1 
              variants={fadeInUp}
              className="text-4xl sm:text-5xl lg:text-6xl font-bold leading-tight mb-6"
            >
              <span className="text-white">Your Certified Cloud & AI</span>
              <br />
              <motion.span 
                className="text-transparent bg-clip-text bg-gradient-to-r from-violet-400 via-cyan-400 to-violet-400"
                animate={{ backgroundPosition: ['0%', '100%', '0%'] }}
                transition={{ duration: 5, repeat: Infinity }}
                style={{ backgroundSize: '200% 200%' }}
              >
                Consulting Partner
              </motion.span>
              
            </motion.h1>

            {/* Subheadline */}
            <motion.p 
              variants={fadeInUp}
              className="text-lg text-slate-400 mb-4 max-w-2xl mx-auto"
            >
              Cloud Consulting • AI Automation • Web Development • Marketing & SEO
            </motion.p>

            <motion.p 
              variants={fadeInUp}
              className="text-base text-slate-500 mb-10 max-w-xl mx-auto"
            >
              Scaling businesses with AI & Cloud. Training the next generation for industry-leading certifications.
            </motion.p>

            {/* CTA Buttons */}
            <motion.div 
              variants={fadeInUp}
              className="flex flex-col sm:flex-row items-center justify-center gap-4"
            >
              <motion.a
                href="/contact"
                onClick={(e) => handleNavClick(e, '/contact')}
                data-testid="hero-cta-consultation"
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-gradient-to-r from-violet-600 to-blue-600 text-white font-medium transition-all"
                whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(124, 58, 237, 0.5)' }}
                whileTap={{ scale: 0.98 }}
              >
                Book Free Consultation
                <ArrowRight size={18} />
              </motion.a>
              <motion.a
                href="/services"
                onClick={(e) => handleNavClick(e, '/services')}
                data-testid="hero-cta-project"
                className="inline-flex items-center gap-2 h-12 px-8 rounded-full border border-white/20 bg-white/5 text-white font-medium hover:bg-white/10 transition-all"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                See What We Build
                <ChevronRight size={18} />
              </motion.a>
            </motion.div>

            {/* GCP Badge */}
            <motion.div 
              variants={fadeInUp} 
              className="mt-12 flex justify-center"
            >
              <motion.img 
                src={GCP_BADGE} 
                alt="Google Cloud Partner" 
                className="h-14 w-auto rounded-lg bg-white p-2"
                data-testid="hero-gcp-badge"
                whileHover={{ scale: 1.05 }}
              />
            </motion.div>
          </motion.div>
        </div>

        {/* Scroll Indicator */}
       
      </section>

      {/* Google Cloud Partner Section */}
      <section className="py-12 relative overflow-hidden" data-testid="gcp-partner-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="flex flex-col md:flex-row items-center justify-center gap-8 p-8 rounded-3xl bg-gradient-to-r from-slate-900/80 via-slate-900/60 to-slate-900/80 border border-white/10"
          >
            <motion.img 
              src={GCP_BADGE} 
              alt="Google Cloud Partner" 
              className="h-20 w-auto rounded-xl bg-white p-3 shadow-lg"
              whileHover={{ scale: 1.05, rotate: 2 }}
            />
            <div className="text-center md:text-left">
              <h3 className="text-xl font-bold text-white mb-2">Official Google Cloud Partner</h3>
              <p className="text-slate-400 max-w-md">
                Certified expertise in Google Cloud Platform. We design, deploy, and optimize 
                cloud infrastructure that scales with your business.
              </p>
            </div>
            <motion.a
              href="/services#cloud-consulting"
              onClick={(e) => handleNavClick(e, '/services')}
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white/10 border border-white/20 text-white font-medium hover:bg-white/20 transition-all whitespace-nowrap"
              whileHover={{ scale: 1.02 }}
            >
              <Cloud size={18} />
              Explore Services
            </motion.a>
          </motion.div>
        </div>
      </section>

      {/* Training Highlight */}
      {/* 
      <section className="py-24 relative bg-gradient-to-b from-violet-500/5 to-transparent" data-testid="training-highlight-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="rounded-3xl border border-white/10 bg-slate-900/30 p-8 md:p-12 overflow-hidden relative"
            whileHover={{ borderColor: 'rgba(124, 58, 237, 0.3)' }}
          >
            <motion.div 
              className="absolute -top-40 -right-40 w-80 h-80 bg-violet-500/20 rounded-full blur-[100px]"
              animate={{ scale: [1, 1.2, 1], opacity: [0.3, 0.5, 0.3] }}
              transition={{ duration: 6, repeat: Infinity }}
            />
            
            <div className="relative grid lg:grid-cols-2 gap-12 items-center">
              <div>
                <span className="text-cyan-400 text-sm font-medium uppercase tracking-wider mb-4 block">
                  Career Accelerator
                </span>
                <h2 className="text-3xl sm:text-4xl font-bold text-white mb-6">
                  Expert-Led Training & <br />
                  <span className="text-transparent bg-clip-text bg-gradient-to-r from-cyan-400 to-violet-400">
                    Global Certification
                  </span>
                </h2>
                <p className="text-slate-400 leading-relaxed mb-6">
                  Our training programs go beyond theory. Learn from <strong>Industry Practitioners </strong>  
                   through hands-on projects and earn the <strong>Professional Certificates.</strong> 
                  
                </p>
                
                <ul className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8">
                  {[
                    'Practitioner-Led Mentorship', 
                    'Enterprise-Scale Projects', 
                    'Global Certification Prep', 
                    'Career Placement Strategy'
                  ].map((item, idx) => (
                    <motion.li 
                      key={item} 
                      className="flex items-center gap-3 text-slate-300 text-sm"
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      viewport={{ once: true }}
                      transition={{ delay: idx * 0.1 }}
                    >
                      <CheckCircle size={18} className="text-cyan-400" />
                      {item}
                    </motion.li>
                  ))}
                </ul>

                <motion.a
                  href="/training"
                  onClick={(e) => handleNavClick(e, '/training')}
                  className="inline-flex items-center gap-2 h-12 px-8 rounded-full bg-gradient-to-r from-cyan-600 to-blue-600 text-white font-medium transition-all"
                  whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(6, 182, 212, 0.5)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  Explore All Programs
                  <ArrowRight size={18} />
                </motion.a>
              </div>

              <div className="grid grid-cols-2 gap-4">
                {[
                  { name: 'Cloud & DevOps', color: 'from-blue-400 to-cyan-400' },
                  { name: 'AI & Data Science', color: 'from-violet-400 to-purple-400' },
                  { name: 'Full-Stack Dev', color: 'from-emerald-400 to-teal-400' },
                  { name: 'Digital Strategy', color: 'from-orange-400 to-yellow-400' }
                ].map((course, idx) => (
                  <motion.div 
                    key={course.name}
                    className="p-5 rounded-2xl bg-slate-950/50 border border-white/10 hover:border-white/30 transition-all text-center group"
                    whileHover={{ y: -5, scale: 1.02 }}
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: idx * 0.1 }}
                  >
                    <div className={`text-2xl font-bold text-transparent bg-clip-text bg-gradient-to-r ${course.color} mb-2`}>
                      {String(idx + 1).padStart(2, '0')}
                    </div>
                    <div className="text-white font-medium text-xs sm:text-sm group-hover:text-white/90">
                      {course.name}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </motion.div>
        </div>
      </section>
      */}

      {/* Services Section */}
      {/* Services Section */}
<section className="py-24 relative overflow-hidden" data-testid="services-section">
  <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center mb-16"
    >
      <span className="text-cyan-400 text-sm font-medium uppercase tracking-wider mb-4 block">What We Do</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Solutions That Actually Solve Problems
            </h2>
    
      <p className="text-slate-400 max-w-2xl mx-auto text-lg leading-relaxed">
        We build the technical foundation your business needs to lead in a digital-first world.
      </p>
    </motion.div>

    <motion.div 
      className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16"
      variants={staggerContainer}
      initial="hidden"
      whileInView="visible"
      viewport={{ once: true }}
    >
      {services.map((service) => (
        <motion.div
          key={service.title}
          variants={fadeInUp}
          className="group relative p-10 rounded-[2rem] bg-slate-900/40 border border-white/5 hover:border-violet-500/40 transition-all duration-500 overflow-hidden"
          whileHover={{ y: -10 }}
        >
          {/* Subtle Hover Glow Background */}
          <div className="absolute inset-0 bg-gradient-to-br from-violet-600/5 to-cyan-600/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
          
          <div className="relative z-10">
            <div className={`w-16 h-16 rounded-2xl flex items-center justify-center mb-8 bg-slate-950 border border-white/10 group-hover:border-violet-500/50 transition-colors shadow-xl`}>
              <service.icon size={32} className={`${
                service.color === 'violet' ? 'text-violet-400' :
                service.color === 'cyan' ? 'text-cyan-400' :
                service.color === 'blue' ? 'text-blue-400' : 'text-teal-400'
              }`} />
            </div>

            <h3 className="text-2xl font-bold text-white mb-4 group-hover:text-violet-300 transition-colors">
              {service.title}
            </h3>
            
            <p className="text-slate-400 leading-relaxed mb-8 text-sm md:text-base">
              {service.description}
            </p>

            <motion.a 
              href="/services"
              onClick={(e) => handleNavClick(e, '/services')}
              className="inline-flex items-center gap-2 text-sm font-semibold text-white/70 group-hover:text-white transition-colors"
              whileHover={{ x: 5 }}
            >
              Learn More <ArrowRight size={16} className="text-violet-400" />
            </motion.a>
          </div>
        </motion.div>
      ))}
    </motion.div>

    {/* Explore All Services Button */}
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      className="text-center"
    >
      <motion.a
        href="/services"
        onClick={(e) => handleNavClick(e, '/services')}
        className="inline-flex items-center gap-3 h-14 px-10 rounded-full bg-gradient-to-r from-violet-600 via-blue-600 to-cyan-600 text-white font-bold text-base transition-all shadow-[0_0_20px_rgba(124,58,237,0.3)] hover:shadow-[0_0_40px_rgba(124,58,237,0.6)]"
        whileHover={{ scale: 1.05 }}
        whileTap={{ scale: 0.95 }}
      >
        Explore All Services
        <ArrowRight size={20} />
      </motion.a>
    </motion.div>
  </div>
</section>
      {/* Why Choose Us */}
      <section className="py-24 relative" data-testid="why-choose-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-violet-400 text-sm font-medium uppercase tracking-wider mb-4 block">Why BikerTechie</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              What Makes Us Different
            </h2>
            <p className="text-slate-400 max-w-2xl mx-auto">
              We combine deep technical expertise with genuine business understanding.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {whyChooseUs.map((item, index) => (
              <motion.div
                key={item.title}
                variants={fadeInUp}
                className="p-6 rounded-2xl bg-slate-900/30 border border-white/5 hover:border-violet-500/20 transition-all text-center group"
                whileHover={{ y: -5 }}
              >
                <motion.div 
                  className="w-12 h-12 rounded-xl bg-violet-500/10 flex items-center justify-center mx-auto mb-4"
                  whileHover={{ rotate: 10, scale: 1.1 }}
                >
                  <item.icon size={24} className="text-violet-400" />
                </motion.div>
                <h3 className="text-white font-semibold mb-2">{item.title}</h3>
                <p className="text-slate-500 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      

      {/* Testimonials */}
      <section className="py-24 relative" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <span className="text-violet-400 text-sm font-medium uppercase tracking-wider mb-4 block">Client Stories</span>
            <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
              Don't Take Our Word For It
            </h2>
            <p className="text-slate-400 max-w-xl mx-auto">
              Here's what our clients say about working with us.
            </p>
          </motion.div>

          <motion.div 
            className="grid md:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {testimonials.map((testimonial, index) => (
              <motion.div
                key={testimonial.author}
                variants={fadeInUp}
                className="p-8 rounded-3xl bg-slate-900/50 border border-white/10 hover:border-violet-500/20 transition-all"
                data-testid={`testimonial-${index}`}
                whileHover={{ y: -5 }}
              >
                <div className="flex gap-1 mb-4">
                  {[...Array(testimonial.rating)].map((_, i) => (
                    <Star key={i} size={16} className="text-yellow-500 fill-yellow-500" />
                  ))}
                </div>
                <p className="text-slate-300 leading-relaxed mb-6">"{testimonial.quote}"</p>
                <div>
                  <div className="text-white font-medium">{testimonial.author}</div>
                  <div className="text-slate-500 text-sm">{testimonial.role}</div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 relative" data-testid="final-cta-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="rounded-3xl bg-gradient-to-r from-violet-600/20 via-blue-600/20 to-cyan-600/20 border border-white/10 p-12 md:p-20 text-center relative overflow-hidden"
          >
            {/* Animated background elements */}
            <motion.div 
              className="absolute top-0 left-1/4 w-40 h-40 bg-violet-500/30 rounded-full blur-[60px]"
              animate={{ y: [0, -20, 0], scale: [1, 1.2, 1] }}
              transition={{ duration: 5, repeat: Infinity }}
            />
            <motion.div 
              className="absolute bottom-0 right-1/4 w-40 h-40 bg-cyan-500/30 rounded-full blur-[60px]"
              animate={{ y: [0, 20, 0], scale: [1.2, 1, 1.2] }}
              transition={{ duration: 5, repeat: Infinity }}
            />
            
            <div className="relative">
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-6">
                Ready to Build Something Great?
              </h2>
              <p className="text-slate-300 text-lg mb-10 max-w-2xl mx-auto">
                Let's talk about your challenges and explore how we can help. 
                No pressure, no sales pitch—just an honest conversation.
              </p>
              <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
                <motion.a
                  href="/contact"
                  onClick={(e) => handleNavClick(e, '/contact')}
                  data-testid="final-cta-consultation"
                  className="inline-flex items-center gap-2 h-14 px-10 rounded-full bg-white text-slate-900 font-semibold text-base transition-all"
                  whileHover={{ scale: 1.02, boxShadow: '0 0 30px rgba(255, 255, 255, 0.3)' }}
                  whileTap={{ scale: 0.98 }}
                >
                  Book Free Consultation
                  <ArrowRight size={20} />
                </motion.a>
                <motion.a
                  href="/services"
                  onClick={(e) => handleNavClick(e, '/services')}
                  data-testid="final-cta-services"
                  className="inline-flex items-center gap-2 h-14 px-10 rounded-full border border-white/30 text-white font-semibold text-base hover:bg-white/10 transition-all"
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Explore Services
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Home;
